/**
 * ProgressiveProfileContext.tsx
 *
 * Manages contextual, non-blocking data collection after onboarding.
 * Tracks which prompts have been shown, skipped, or completed.
 * Stores state in localStorage and syncs to Supabase on completion.
 *
 * Rules:
 *  - Max ONE prompt per session
 *  - Wait ≥ 2 sessions before re-prompting after a skip
 *  - Never show a full-screen interrupt
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useUserPreferences } from "./UserPreferencesContext";
import { parseUserTopic, describeInference, type ParsedTopic } from "../lib/parseUserTopic";

// ─── Types ────────────────────────────────────────────────────────────────────

export type PromptStatus = "pending" | "shown" | "completed" | "skipped";

export interface ProgressiveProfileState {
  /** How many study sessions the user has completed */
  sessionCount: number;
  /** Session number when each prompt was last shown */
  lastShownSession: Record<string, number>;
  /** Status of each prompt */
  prompts: {
    subjects: PromptStatus;
    goals: PromptStatus;
    subjectConfirm: PromptStatus;
  };
  /** Collected data (inferred or confirmed) */
  data: {
    subjects: string[];
    goal: string | null;
    /** Inferred from topic input */
    inferredSubject: string | null;
    inferredExam: string | null;
    /** 'inferred' | 'confirmed' | null */
    subjectSource: "inferred" | "confirmed" | null;
    pendingTopic: string | null;
    parsedTopic: ParsedTopic | null;
  };
  /** Which prompt is currently showing (only one at a time) */
  activePrompt: string | null;
}

export interface ProgressiveProfileContextType {
  state: ProgressiveProfileState;
  /** Call when user generates their first quiz/flashcard */
  onStudyAction: (topic?: string) => void;
  /** Call when user completes a quiz/flashcard session */
  onSessionComplete: () => void;
  /** Returns true if we should show this prompt right now */
  shouldShowPrompt: (key: keyof ProgressiveProfileState["prompts"]) => boolean;
  /** Mark a prompt as shown (starts cooldown) */
  markShown: (key: keyof ProgressiveProfileState["prompts"]) => void;
  /** Mark a prompt completed and persist the data */
  completeSubjects: (subjects: string[]) => Promise<void>;
  completeGoal: (goal: string) => Promise<void>;
  confirmSubject: (confirmed: boolean, override?: string) => Promise<void>;
  /** Dismiss without completing — respects skip cooldown */
  skipPrompt: (key: keyof ProgressiveProfileState["prompts"]) => void;
  /** Store the topic entered during quick-win onboarding */
  storePendingTopic: (topic: string) => void;
  /** Consume the pending topic (returns it and clears) */
  consumePendingTopic: () => string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = "novari_progressive_profile_v2";
const MIN_SESSIONS_BEFORE_REPROMPT = 2;

const DEFAULT_STATE: ProgressiveProfileState = {
  sessionCount: 0,
  lastShownSession: {},
  prompts: {
    subjects: "pending",
    goals: "pending",
    subjectConfirm: "pending",
  },
  data: {
    subjects: [],
    goal: null,
    inferredSubject: null,
    inferredExam: null,
    subjectSource: null,
    pendingTopic: null,
    parsedTopic: null,
  },
  activePrompt: null,
};

// ─── Context ──────────────────────────────────────────────────────────────────

const ProgressiveProfileContext = createContext<ProgressiveProfileContextType | undefined>(
  undefined
);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ProgressiveProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { updatePreferences } = useUserPreferences();
  const [state, setState] = useState<ProgressiveProfileState>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }
    return DEFAULT_STATE;
  });

  // Persist every state change to localStorage
  const persist = useCallback((next: ProgressiveProfileState) => {
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);

  // ── Sync answers to Supabase (non-blocking) ────────────────────────────────
  // subjects / study_goal live on user_preferences and are written through
  // the same RPC as Settings. This used to .update() profiles with columns
  // that don't exist there (subjects, study_goal, inferred_*), so PostgREST
  // rejected every write and the error was swallowed. Inferred topic data
  // has no column and stays in localStorage only.
  const syncToSupabase = useCallback(
    async (updates: { subjects?: string[]; study_goal?: string }) => {
      if (!user?.id) return;
      try {
        await updatePreferences(updates);
      } catch {
        /* non-blocking: updatePreferences already logged and rolled back */
      }
    },
    [user?.id, updatePreferences]
  );

  // ── Store pending topic (called from QuickWin step) ───────────────────────
  const storePendingTopic = useCallback(
    (topic: string) => {
      const parsed = parseUserTopic(topic);
      const next: ProgressiveProfileState = {
        ...state,
        data: {
          ...state.data,
          pendingTopic: topic,
          parsedTopic: parsed,
          inferredSubject: parsed.subject ?? state.data.inferredSubject,
          inferredExam: parsed.examContext ?? state.data.inferredExam,
          subjectSource: parsed.subject ? "inferred" : state.data.subjectSource,
        },
        // If we inferred a subject with high/medium confidence, flag it for confirmation
        prompts: {
          ...state.prompts,
          subjectConfirm:
            parsed.subject && parsed.confidence !== "low"
              ? "pending"
              : state.prompts.subjectConfirm,
        },
      };
      persist(next);
    },
    [state, persist]
  );

  const consumePendingTopic = useCallback((): string | null => {
    const topic = state.data.pendingTopic;
    if (topic) {
      persist({
        ...state,
        data: { ...state.data, pendingTopic: null },
      });
    }
    return topic;
  }, [state, persist]);

  // ── Study action trigger (first quiz/flashcard generation) ────────────────
  const onStudyAction = useCallback(
    (topic?: string) => {
      // If a topic is provided, parse and store it
      if (topic) {
        storePendingTopic(topic);
        return;
      }
    },
    [storePendingTopic]
  );

  // ── Session completion ─────────────────────────────────────────────────────
  const onSessionComplete = useCallback(() => {
    persist({
      ...state,
      sessionCount: state.sessionCount + 1,
    });
  }, [state, persist]);

  // ── Prompt visibility logic ───────────────────────────────────────────────
  const shouldShowPrompt = useCallback(
    (key: keyof ProgressiveProfileState["prompts"]): boolean => {
      const status = state.prompts[key];
      if (status === "completed") return false;
      if (state.activePrompt && state.activePrompt !== key) return false;

      const lastShown = state.lastShownSession[key] ?? -Infinity;
      const sessionsSinceLast = state.sessionCount - lastShown;

      if (status === "skipped" && sessionsSinceLast < MIN_SESSIONS_BEFORE_REPROMPT) {
        return false;
      }

      // Specific trigger conditions
      switch (key) {
        case "subjectConfirm":
          // Show after first study session if we have an inferred subject
          return (
            !!state.data.inferredSubject &&
            state.data.subjectSource === "inferred" &&
            state.sessionCount >= 1
          );
        case "subjects":
          // Show after first session if no subjects set
          return (
            state.data.subjects.length === 0 &&
            state.prompts.subjectConfirm !== "pending" &&
            state.sessionCount >= 1
          );
        case "goals":
          // Show after first session completes if no goal set
          return !state.data.goal && state.sessionCount >= 1;
        default:
          return false;
      }
    },
    [state]
  );

  const markShown = useCallback(
    (key: keyof ProgressiveProfileState["prompts"]) => {
      persist({
        ...state,
        activePrompt: key,
        lastShownSession: {
          ...state.lastShownSession,
          [key]: state.sessionCount,
        },
        prompts: { ...state.prompts, [key]: "shown" },
      });
    },
    [state, persist]
  );

  const skipPrompt = useCallback(
    (key: keyof ProgressiveProfileState["prompts"]) => {
      persist({
        ...state,
        activePrompt: null,
        prompts: { ...state.prompts, [key]: "skipped" },
      });
    },
    [state, persist]
  );

  // ── Completions ───────────────────────────────────────────────────────────

  const completeSubjects = useCallback(
    async (subjects: string[]) => {
      const next: ProgressiveProfileState = {
        ...state,
        activePrompt: null,
        prompts: { ...state.prompts, subjects: "completed" },
        data: { ...state.data, subjects },
      };
      persist(next);
      await syncToSupabase({ subjects });
    },
    [state, persist, syncToSupabase]
  );

  const completeGoal = useCallback(
    async (goal: string) => {
      const next: ProgressiveProfileState = {
        ...state,
        activePrompt: null,
        prompts: { ...state.prompts, goals: "completed" },
        data: { ...state.data, goal },
      };
      persist(next);
      await syncToSupabase({ study_goal: goal });
    },
    [state, persist, syncToSupabase]
  );

  const confirmSubject = useCallback(
    async (confirmed: boolean, override?: string) => {
      const finalSubject =
        override ?? (confirmed ? state.data.inferredSubject : null);
      const next: ProgressiveProfileState = {
        ...state,
        activePrompt: null,
        prompts: { ...state.prompts, subjectConfirm: "completed" },
        data: {
          ...state.data,
          inferredSubject: finalSubject,
          subjectSource: finalSubject ? "confirmed" : null,
          subjects:
            finalSubject && !state.data.subjects.includes(finalSubject)
              ? [...state.data.subjects, finalSubject]
              : state.data.subjects,
        },
      };
      persist(next);
      if (finalSubject) {
        await syncToSupabase({ subjects: next.data.subjects });
      }
    },
    [state, persist, syncToSupabase]
  );

  const value = useMemo<ProgressiveProfileContextType>(
    () => ({
      state,
      onStudyAction,
      onSessionComplete,
      shouldShowPrompt,
      markShown,
      completeSubjects,
      completeGoal,
      confirmSubject,
      skipPrompt,
      storePendingTopic,
      consumePendingTopic,
    }),
    [
      state,
      onStudyAction,
      onSessionComplete,
      shouldShowPrompt,
      markShown,
      completeSubjects,
      completeGoal,
      confirmSubject,
      skipPrompt,
      storePendingTopic,
      consumePendingTopic,
    ]
  );

  return (
    <ProgressiveProfileContext.Provider value={value}>
      {children}
    </ProgressiveProfileContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useProgressiveProfile(): ProgressiveProfileContextType {
  const ctx = useContext(ProgressiveProfileContext);
  if (!ctx) throw new Error("useProgressiveProfile must be inside ProgressiveProfileProvider");
  return ctx;
}

export { describeInference };
